import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import './OrganizationInfoSection.css';

function OrganizationInfoSection({ organizationId }) {
    const { canManage } = usePermissions();
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const logoInputRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postal_code: '',
        country: '',
        website: '',
        primary_color: '#4f46e5',
        secondary_color: '#6b7280'
    });

    useEffect(() => {
        loadOrganizationInfo();
    }, [organizationId]);

    const loadOrganizationInfo = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await API.getOrganizationInfo(organizationId);
            if (response && response.data && response.data.organization) {
                const org = response.data.organization;
                setOrganization(org);
                setFormData({
                    name: org.name || '',
                    domain: org.domain || '',
                    phone: org.phone || '',
                    email: org.email || '',
                    address: org.address || '',
                    city: org.city || '',
                    postal_code: org.postal_code || '',
                    country: org.country || '',
                    website: org.website || '',
                    primary_color: org.primary_color || '#4f46e5',
                    secondary_color: org.secondary_color || '#6b7280'
                });
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Vérifier le type de fichier
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Type de fichier non autorisé. Formats acceptés: JPG, PNG, GIF, SVG, WebP');
            return;
        }

        // Vérifier la taille (max 2 Mo)
        if (file.size > 2 * 1024 * 1024) {
            setError('Le fichier est trop volumineux. Taille maximale: 2 Mo');
            return;
        }

        setUploadingLogo(true);
        setError('');

        try {
            const response = await API.uploadOrganizationLogo(organizationId, file);
            if (response && response.data) {
                setOrganization(prev => ({ ...prev, logo_url: response.data.logo_url }));
                setSuccess('Logo uploadé avec succès');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setUploadingLogo(false);
            if (logoInputRef.current) {
                logoInputRef.current.value = '';
            }
        }
    };

    const handleDeleteLogo = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
            return;
        }

        setUploadingLogo(true);
        setError('');

        try {
            await API.deleteOrganizationLogo(organizationId);
            setOrganization(prev => ({ ...prev, logo_url: null }));
            setSuccess('Logo supprimé avec succès');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError(error.message);
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const response = await API.updateOrganizationInfo(organizationId, formData);
            if (response && response.data) {
                setOrganization(response.data.organization);
                setSuccess('Informations mises à jour avec succès');
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="organization-info-section loading">Chargement...</div>;
    }

    return (
        <div className="organization-info-section">
            <div className="section-header">
                <h2>Informations de l'entreprise</h2>
                <p className="section-description">
                    Ces informations peuvent être utilisées dans vos signatures email.
                </p>
            </div>

            {error && <div className="message error">{error}</div>}
            {success && <div className="message success">{success}</div>}

            <form onSubmit={handleSubmit} className="organization-form">
                <div className="form-section">
                    <h3>Informations générales</h3>

                    <div className="form-group">
                        <label htmlFor="name">Nom de l'organisation *</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            disabled={!canManage}
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="domain">Domaine email</label>
                            <input
                                type="text"
                                id="domain"
                                name="domain"
                                value={formData.domain}
                                onChange={handleChange}
                                placeholder="exemple.com"
                                disabled={!canManage}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="website">Site web</label>
                            <input
                                type="url"
                                id="website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://www.exemple.com"
                                disabled={!canManage}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Coordonnées</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="phone">Téléphone</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+33 1 23 45 67 89"
                                disabled={!canManage}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email de contact</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="contact@exemple.com"
                                disabled={!canManage}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Adresse</h3>

                    <div className="form-group">
                        <label htmlFor="address">Adresse</label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="123 rue de l'Exemple"
                            disabled={!canManage}
                        />
                    </div>

                    <div className="form-row three-cols">
                        <div className="form-group">
                            <label htmlFor="postal_code">Code postal</label>
                            <input
                                type="text"
                                id="postal_code"
                                name="postal_code"
                                value={formData.postal_code}
                                onChange={handleChange}
                                placeholder="75001"
                                disabled={!canManage}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="city">Ville</label>
                            <input
                                type="text"
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="Paris"
                                disabled={!canManage}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="country">Pays</label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="France"
                                disabled={!canManage}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Charte graphique</h3>
                    <p className="form-section-description">
                        Le logo et les couleurs de votre organisation seront disponibles dans le builder de signatures.
                    </p>

                    <div className="form-group">
                        <label>Logo de l'organisation</label>
                        <div className="logo-upload-container">
                            <div className="logo-preview">
                                {organization?.logo_url ? (
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL || ''}${organization.logo_url}`}
                                        alt="Logo de l'organisation"
                                    />
                                ) : (
                                    <div className="logo-placeholder">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                            <circle cx="8.5" cy="8.5" r="1.5"/>
                                            <polyline points="21,15 16,10 5,21"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <div className="logo-actions">
                                <input
                                    type="file"
                                    ref={logoInputRef}
                                    onChange={handleLogoUpload}
                                    accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                                    style={{ display: 'none' }}
                                    disabled={!canManage || uploadingLogo}
                                />
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={!canManage || uploadingLogo}
                                >
                                    {uploadingLogo ? 'Upload...' : (organization?.logo_url ? 'Changer le logo' : 'Ajouter un logo')}
                                </button>
                                {organization?.logo_url && (
                                    <button
                                        type="button"
                                        className="btn-danger-outline"
                                        onClick={handleDeleteLogo}
                                        disabled={!canManage || uploadingLogo}
                                    >
                                        Supprimer
                                    </button>
                                )}
                                <span className="logo-hint">PNG, JPG, SVG, WebP. Max 2 Mo.</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="primary_color">Couleur primaire</label>
                            <div className="color-input-wrapper">
                                <input
                                    type="color"
                                    id="primary_color"
                                    name="primary_color"
                                    value={formData.primary_color}
                                    onChange={handleChange}
                                    disabled={!canManage}
                                    className="color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.primary_color}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                            setFormData(prev => ({ ...prev, primary_color: value }));
                                        }
                                    }}
                                    disabled={!canManage}
                                    className="color-text-input"
                                    maxLength={7}
                                />
                            </div>
                            <span className="form-hint">Utilisée pour les éléments principaux (titres, boutons...)</span>
                        </div>

                        <div className="form-group">
                            <label htmlFor="secondary_color">Couleur secondaire</label>
                            <div className="color-input-wrapper">
                                <input
                                    type="color"
                                    id="secondary_color"
                                    name="secondary_color"
                                    value={formData.secondary_color}
                                    onChange={handleChange}
                                    disabled={!canManage}
                                    className="color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.secondary_color}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                            setFormData(prev => ({ ...prev, secondary_color: value }));
                                        }
                                    }}
                                    disabled={!canManage}
                                    className="color-text-input"
                                    maxLength={7}
                                />
                            </div>
                            <span className="form-hint">Utilisée pour les éléments secondaires (sous-titres, icônes...)</span>
                        </div>
                    </div>
                </div>

                {canManage && (
                    <div className="form-actions">
                        <button type="submit" className="btn-primary" disabled={saving}>
                            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}

export default OrganizationInfoSection;
